
import mixpanel from 'mixpanel-browser';
import { notify } from "../lib/toast";

// Types
export type EventName =
    | 'marketplace_viewed'
    | 'search_performed'
    | 'filter_applied'
    | 'listing_viewed'
    | 'contact_seller_clicked'
    | 'listing_impression'
    | 'purchase_initiated'
    | 'purchase_completed';

interface EventProperties {
    [key: string]: any;
}

// Config 
const GA4_ID = 'G-XXXXXXXXXX'; // Placeholder
const MIXPANEL_TOKEN = (import.meta as any).env.VITE_MIXPANEL_TOKEN;

// Internal State for Deduplication
const impressionCache = new Set<string>();

class AnalyticsService {
    private initialized = false;

    init() {
        if (this.initialized) return;

        // Initialize GA4
        if (typeof window !== 'undefined' && !(window as any).gtag) {
            // console.warn("GA4 script missing in index.html");
        }

        // Initialize Mixpanel
        try {
            if (MIXPANEL_TOKEN) {
                mixpanel.init(MIXPANEL_TOKEN, {
                    debug: (import.meta as any).env.DEV,
                    track_pageview: true,
                    persistence: 'localStorage',
                    ignore_dnt: true
                });
            } else {
                console.warn("Mixpanel Token missing");
            }
        } catch (e) {
            console.error("Mixpanel init failed", e);
        }

        this.initialized = true;
        console.log("Analytics Service Initialized 📊");
    }

    trackEvent(name: EventName, props: EventProperties = {}) {
        if (!this.initialized) this.init();

        // 1. Data Cleaning & Normalization
        const cleanProps = {
            ...props,
            timestamp: Date.now(),
            path: window.location.pathname,
        };

        // 2. Rate Limiting for Impressions
        if (name === 'listing_impression') {
            const impressionKey = `${props.listing_id}_${window.location.pathname}`;
            if (impressionCache.has(impressionKey)) {
                return; // Skip duplicate
            }
            impressionCache.add(impressionKey);
        }

        // 3. Console Debug (Dev Mode)
        if ((import.meta as any).env.DEV) {
            console.groupCollapsed(`📊 [Analytics] ${name}`);
            console.log(cleanProps);
            console.groupEnd();
        }

        // 4. Dispatch to Providers

        // GA4
        if ((window as any).gtag) {
            try {
                (window as any).gtag('event', name, cleanProps);
            } catch (e) { console.error("GA4 Track Error", e); }
        }

        // Mixpanel
        if (mixpanel && mixpanel.track && MIXPANEL_TOKEN) {
            try {
                mixpanel.track(name, cleanProps);
            } catch (e) {
                console.error("Mixpanel Track Error", e);
            }
        }
    }

    identifyUser(userId: string, traits: any = {}) {
        if ((import.meta as any).env.DEV) console.log(`👤 [Analytics] Identify: ${userId}`, traits);

        try {
            if (mixpanel && mixpanel.identify && MIXPANEL_TOKEN) {
                mixpanel.identify(userId);
                mixpanel.people.set(traits);
            }
        } catch (e) { console.error("Mixpanel Identify Error", e); }

        if ((window as any).gtag) {
            (window as any).gtag('config', GA4_ID, { 'user_id': userId });
        }
    }
}

export const analytics = new AnalyticsService();
